/**
 * Public contact form — name / email / subject / message.
 *
 * Used on both the platform landing (``orgSlug`` omitted, message
 * lands on the platform admin Kanban inbox) and the tenant landing
 * (``orgSlug`` from ``useTenantBranding().data?.slug``, message
 * lands on that tenant's Kanban inbox).
 *
 * Submits to ``POST /api/v1/contact``. The endpoint persists the
 * row unconditionally and tries to email ``support@`` best-effort,
 * so even on a Resend outage the message reaches the admin Kanban.
 */

import { Button } from "@/ingot";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SubmitBlockers, useSubmitGuard } from "@/components/ui/SubmitGuard";
import { apiFetch } from "@/lib/api";

interface ContactFormProps {
  /** Tenant slug when rendered on a tenant landing; omitted on the
   *  platform homepage so the submission goes to ``org_id=NULL``. */
  orgSlug?: string;
}

interface ContactResponse {
  id: number;
}

export function ContactForm({ orgSlug }: ContactFormProps): JSX.Element {
  const { t } = useTranslation("common");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitState, setSubmitState] = useState<
    "idle" | "pending" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const guard = useSubmitGuard();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitState === "pending") return;
    if (
      !guard.check(
        !name.trim() &&
          t("home.contact.validation.nameRequired", {
            defaultValue: "Vyplňte jméno.",
          }),
        !email.trim() &&
          t("home.contact.validation.emailRequired", {
            defaultValue: "Vyplňte e-mail.",
          }),
        !subject.trim() &&
          t("home.contact.validation.subjectRequired", {
            defaultValue: "Vyplňte předmět.",
          }),
        !message.trim() &&
          t("home.contact.validation.messageRequired", {
            defaultValue: "Napište zprávu.",
          }),
      )
    ) {
      return;
    }
    setSubmitState("pending");
    setErrorMessage(null);
    try {
      await apiFetch<ContactResponse>("/api/v1/contact", {
        method: "POST",
        body: {
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
          ...(orgSlug ? { org_slug: orgSlug } : {}),
        },
      });
      setSubmitState("success");
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch (err) {
      setSubmitState("error");
      setErrorMessage(
        err instanceof Error
          ? err.message
          : t("home.contact.errorFallback", {
              defaultValue: "Odeslání selhalo, zkuste to prosím znovu.",
            }),
      );
    }
  }

  return (
    <section
      id="kontakt"
      className="border-t border-border bg-surface"
      data-testid="landing-contact-section"
    >
      <div className="mx-auto max-w-3xl px-7 py-20">
        <header className="mb-8 text-center">
          <h2 className="text-balance text-3xl font-semibold tracking-tight text-ink">
            {t("home.contact.heading", { defaultValue: "Kontaktujte nás" })}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-ink-2">
            {t("home.contact.lead", {
              defaultValue:
                "Napište nám zprávu — odpovíme obvykle do jednoho pracovního dne.",
            })}
          </p>
        </header>

        {submitState === "success" ? (
          <div
            className="rounded-lg border border-accent/30 bg-accent/5 px-5 py-6 text-center"
            data-testid="contact-form-success"
            role="status"
            aria-live="polite"
          >
            <p className="text-base font-medium text-ink">
              {t("home.contact.successTitle", {
                defaultValue: "Zpráva byla odeslána",
              })}
            </p>
            <p className="mt-1 text-sm text-ink-2">
              {t("home.contact.successLead", {
                defaultValue: "Brzy se vám ozveme.",
              })}
            </p>
            <button
              type="button"
              onClick={() => setSubmitState("idle")}
              className="mt-4 text-sm text-ink-3 underline underline-offset-2 hover:text-ink"
            >
              {t("home.contact.sendAnother", {
                defaultValue: "Poslat další zprávu",
              })}
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="contact-form"
            noValidate
          >
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1 block text-ink-2">
                  {t("home.contact.nameLabel", { defaultValue: "Jméno" })}
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={128}
                  className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-ink shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
                  data-testid="contact-form-name"
                  autoComplete="name"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-ink-2">
                  {t("home.contact.emailLabel", {
                    defaultValue: "Email",
                  })}
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  maxLength={255}
                  className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-ink shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
                  data-testid="contact-form-email"
                  autoComplete="email"
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block text-ink-2">
                {t("home.contact.subjectLabel", {
                  defaultValue: "Předmět",
                })}
              </span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                maxLength={200}
                className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-ink shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
                data-testid="contact-form-subject"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-ink-2">
                {t("home.contact.messageLabel", {
                  defaultValue: "Zpráva",
                })}
              </span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                maxLength={4000}
                rows={6}
                className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-ink shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
                data-testid="contact-form-message"
              />
            </label>

            {submitState === "error" && errorMessage && (
              <p
                className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger"
                role="alert"
                data-testid="contact-form-error"
              >
                {errorMessage}
              </p>
            )}

            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-ink-3">
                {t("home.contact.privacyNote", {
                  defaultValue:
                    "Vaše údaje použijeme pouze k zodpovězení této zprávy.",
                })}
              </p>
              <Button
                type="submit"
                variant="accent"
                size="md"
                className="st-btn"
                loading={submitState === "pending"}
                data-testid="contact-form-submit"
              >
                {t("home.contact.submit", { defaultValue: "Odeslat" })}
              </Button>
            </div>
            <SubmitBlockers blockers={guard.blockers} />
          </form>
        )}
      </div>
    </section>
  );
}
